import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../../components/SEO/SEOHead";
import { useTranslation } from "react-i18next";
import { IoIosArrowBack } from "react-icons/io";
import api from "../../api/api";
import { toast } from "react-toastify";
import { useProfileStore } from "../../store/useUserStore";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

interface Course {
  course_name: string;
  description: string;
  status: "active" | "overdue";
  class_info: {
    id: number;
    branch_name: string;
    class_name: string;
    time_slot: string[];
  }[];
}

interface Student {
  student_name: string;
  course: Course[];
}

const ActiveCourse: React.FC = () => {
  const { t } = useTranslation();
  const { setLoading, loading } = useProfileStore();
  const [studentsData, setStudentsData] = useState<Student[]>([]);

  useEffect(() => {
    fetchActiveCourses();
  }, []);

  const fetchActiveCourses = async () => {
    try {
      setLoading(true);
      const response = await api.get("/student/course");
      if (response.data.code === 200) {
        setStudentsData(response.data.data);
      } else {
        toast.error("Failed to fetch active courses");
      }
    } catch (error) {
      console.error("Error fetching active courses:", error);
      toast.error("Failed to fetch active courses");
    } finally {
      setLoading(false);
    }
  };

  const parseDescription = (description: string) => {
    // Parse description to extract attendance info
    if (description.includes("attendance left")) {
      const match = description.match(/(\d+) attendance left/);
      return {
        type: "left" as const,
        count: match ? parseInt(match[1]) : 0,
        paymentInfo: null,
      };
    } else if (description.includes("attendance overdue")) {
      const match = description.match(/(-?\d+) attendance overdue/);
      return {
        type: "overdue" as const,
        count: match ? Math.abs(parseInt(match[1])) : 0,
        paymentInfo: null,
      };
    } else if (description.startsWith("Paid")) {
      return {
        type: "paid" as const,
        count: 0,
        paymentInfo: description,
      };
    }
    return {
      type: "unknown" as const,
      count: 0,
      paymentInfo: description,
    };
  };

  const CourseCard: React.FC<{
    course: Course;
    classInfo: Course["class_info"][0];
  }> = ({ course, classInfo }) => {
    const parsedInfo = parseDescription(course.description);
    const isOverdue =
      course.status === "overdue" || parsedInfo.type === "overdue";
    const hasAttendanceLeft = parsedInfo.count > 0;

    // Get location and time slots from the specific class_info
    const primaryLocation = classInfo.branch_name || "No location";
    const timeSlots = classInfo.time_slot || [];

    // Calendar related state
    const [calendarExpanded, setCalendarExpanded] = useState(false);

    // Normalize class_dates to a Set of date strings for quick lookup
    const classDates: string[] = (course as any).class_dates || [];
    const classDateSet = new Set(classDates);

    console.log("classDates", classDates);

    // Tile class name function highlights dates that exist in class_dates
    const tileClassName = ({ date, view }: { date: Date; view: string }) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      if (classDateSet.has(formattedDate)) {
        return isOverdue
          ? "bg-red-200 text-red-800 rounded-lg"
          : "bg-green-200 text-green-800 rounded-lg";
      }
      return null;
    };

    return (
      <div
        className={`rounded-lg border-2 p-4 mb-4 cursor-pointer ${
          isOverdue
            ? "border-red-300 bg-red-50"
            : "border-green-300 bg-green-50"
        }`}
        onClick={() => setCalendarExpanded((s) => !s)}
      >
        {/* Location icon and text */}
        <div className="flex items-center mb-2">
          <span
            className={`w-2 h-2 rounded-full mr-2 ${
              isOverdue ? "bg-red-400" : "bg-green-400"
            }`}
          ></span>
          <span className="text-sm text-gray-600">{primaryLocation}</span>
        </div>

        {/* Course name */}
        <h3 className="font-semibold text-gray-900 mb-1">
          {course.course_name}
        </h3>

        {/* Schedule with attendance status */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-gray-600 flex-1 mr-2">
            {timeSlots.length > 0
              ? timeSlots.map((slot, index) => <div key={index}>{slot}</div>)
              : "No schedule available"}
          </div>
          {/* Combined attendance and payment status */}
          {hasAttendanceLeft ? (
            <div
              className={`px-3 py-1 rounded text-xs font-medium whitespace-nowrap ${
                isOverdue
                  ? "bg-red-200 text-red-800"
                  : "bg-green-200 text-green-800"
              }`}
            >
              {isOverdue
                ? `${parsedInfo.count} attn overdue`
                : `${parsedInfo.count} attn left`}
            </div>
          ) : parsedInfo.paymentInfo ? (
            <div
              className={`px-3 py-1 rounded text-xs font-medium whitespace-nowrap ${
                course.status === "overdue"
                  ? "bg-red-200 text-red-800"
                  : "bg-green-200 text-green-800"
              }`}
            >
              {parsedInfo.paymentInfo}
            </div>
          ) : null}
        </div>

        {/* Expandable calendar - shown when CourseCard is clicked, only if class_dates exist */}
        {calendarExpanded && classDates.length > 0 && (
          <div className="mt-3">
            <div
              className="rc-calendar-wrapper flex justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Stop propagation so calendar navigation clicks don't bubble up and collapse the card */}
              <Calendar
                view="month"
                tileClassName={tileClassName as any}
                className="mx-auto"
                // prevent selecting a date since this is a display-only calendar
                onClickDay={() => {}}
                next2Label={null}
                prev2Label={null}
                minDetail="year"
                showNeighboringMonth={false}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const StudentSection: React.FC<{ student: Student }> = ({ student }) => {
    // Only show students who have courses
    if (!student.course || student.course.length === 0) return null;

    return (
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {student.student_name}
        </h2>
        {student.course.map((course, courseIndex) => {
          // If course has class_info, create separate cards for each
          if (course.class_info && course.class_info.length > 0) {
            return course.class_info.map((classInfo, classIndex) => (
              <CourseCard
                key={`${student.student_name}-${courseIndex}-${classIndex}`}
                course={course}
                classInfo={classInfo}
              />
            ));
          } else {
            // If no class_info, create a single card with empty classInfo
            return (
              <CourseCard
                key={`${student.student_name}-${courseIndex}`}
                course={course}
                classInfo={{
                  id: 0,
                  branch_name: "No location",
                  class_name: "No class",
                  time_slot: [],
                }}
              />
            );
          }
        })}
        {/* Separator border - only show if not the last student */}
        <div className="border-b border-gray-200 mt-6"></div>
      </div>
    );
  };

  return (
    <>
      <SEOHead
        title="My Active Course - MYKOKU Parent App"
        description="View your children's active courses, attendance status and payment information"
        keywords="active course, student attendance, course payment, MyKoku"
      />

      <div className="">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="mb-3 grid grid-cols-6 px-4 py-4">
            <div className="self-center">
              <Link to="/cart" className="">
                <IoIosArrowBack size={24} color="#000" />
              </Link>
            </div>

            <div className="text-center col-span-4 text-lg font-medium">
              {t("active_course.title")}
            </div>
            <div></div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 mb-10">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-lg font-medium text-gray-600">
                Loading...
              </div>
            </div>
          ) : studentsData.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-lg font-medium text-gray-600">
                {t("active_course.no_active_course")}
              </div>
            </div>
          ) : (
            studentsData.map((student, index) => (
              <StudentSection
                key={`${student.student_name}-${index}`}
                student={student}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default ActiveCourse;
